// /templates: the app templates I publish and those shared with me (RFC-052
// "Web", byj-1wv.64.5). A template is published from its project, by byj,
// which packs it and leaves its state and secrets out; here it is found,
// given to others, and the line that makes an app from it copied. As /classes
// lists classes (byj-1wv.51.14): rows under "My templates" and "Shared with
// me", both headed whenever there is a template at all, the empty one saying
// so; a template's own actions behind its ⋯, deleting one of mine among them;
// the line that publishes one in a popover from the page's head.
import { useCallback, useEffect, useState } from "react";
import { api, type AppTemplate } from "../api";
import { useConfirm } from "../components/Confirm";
import { Page, PageHead, PageLead } from "../components/Page";
import { Popover } from "../components/Popover";
import { EmptyRows, Grouped } from "../components/Rows";
import { PublishSteps, TemplateRow, templateItems } from "../components/TemplateParts";
import { t } from "../i18n";

const errText = (err: unknown) => (err instanceof Error ? err.message : String(err));

export function Templates({ load = api.appTemplates, remove = api.deleteAppTemplate }: { load?: typeof api.appTemplates; remove?: typeof api.deleteAppTemplate }) {
  const [data, setData] = useState<{ mine: AppTemplate[]; shared: AppTemplate[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ask, confirmDialog] = useConfirm();
  const fetch = useCallback(async () => {
    setError(null);
    try {
      setData(await load());
    } catch (err) {
      setError(errText(err));
    }
  }, [load]);
  useEffect(() => {
    void fetch();
  }, [fetch]);

  const onDelete = async (x: AppTemplate) => {
    if (!(await ask(t("templates:templateDeleteAsk", { name: x.name }), { action: t("templates:templateDelete"), danger: true }))) return;
    try {
      await remove(x.id);
      await fetch();
    } catch (err) {
      setError(errText(err));
    }
  };

  return (
    <Page className="template-page">
      {confirmDialog}
      <PageHead
        title={t("templates:templatesTitle")}
        actions={
          // a template is published from its project, never here: the line that does it
          <Popover label={t("templates:templatePublishTitle")} button={t("templates:templatePublishTitle")} className="pop" popClassName="pop-panel publish-pop" focus="button">
            {() => (
              <div data-testid="publish-pop">
                <PublishSteps />
              </div>
            )}
          </Popover>
        }
      />
      <PageLead>{t("templates:templatesLead")}</PageLead>
      {error && (
        <p className="error">
          {t("common:error", { m: error })} <button className="small" onClick={() => void fetch()}>{t("common:retry")}</button>
        </p>
      )}
      {data === null ? (
        !error && <p className="muted">{t("common:loading")}</p>
      ) : (
        <div className="rows-panel class-groups" data-testid="template-list">
          <Grouped
            id="templates"
            headed
            groups={[
              {
                title: t("templates:templatesMine"),
                items: data.mine,
                none: (
                  <div className="card pad stack no-templates" data-testid="no-templates-mine">
                    <p className="muted flush">{t("templates:noTemplatesMine")}</p>
                    <PublishSteps />
                  </div>
                ),
              },
              {
                title: t("templates:templatesShared"),
                items: data.shared,
                none: (
                  <div data-testid="no-templates-shared">
                    <EmptyRows text={t("templates:noTemplatesShared")} />
                  </div>
                ),
              },
            ]}
            row={(x) => (
              <TemplateRow
                key={x.id}
                x={x}
                menu={[
                  ...templateItems(x),
                  // deleting one is its owner's alone (RFC-052 §5)
                  ...(x.mine ? [{ label: t("templates:templateDelete"), onSelect: () => void onDelete(x), danger: true }] : []),
                ]}
              />
            )}
          />
        </div>
      )}
    </Page>
  );
}
